<template>
    <div>
        <Modal
            ref="modal"
            title="Raise Purchase Invoice"
            size="is-xxxlarge"
            paddingless
            :close-enable="!isLoading && !isUploading"
            :loading="isLoading || isUploading"
            @closed="close()"
        >
            <template #loading>
                <LoadingSpinner v-if="!isUploading" label="Creating invoice" />
                <div v-else class="tw-flex tw-w-full tw-flex-col tw-items-center tw-gap-3 tw-p-5 tw-text-gray-400">
                    <LoadingSpinner label="Uploading attachment" />
                    <div class="tw-w-full tw-max-w-md tw-text-center">
                        <div class="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-sm">
                            <span class="tw-truncate">{{ uploadFileName }}</span>
                            <span class="tw-shrink-0">{{ uploadProgress }}%</span>
                        </div>
                        <ProgressBar class="tw-h-3 tw-shadow-sm" :progress="uploadProgress" />
                    </div>
                </div>
            </template>
            <div class="tw-p-3">
                <FormField
                    class="tw-flex-1"
                    :label="
                        'Invoice Title' +
                        (formData.purchase_orders.length > 1 ? ' (only required for Multi PO invoices)' : '')
                    "
                >
                    <Textbox v-model="formData.title" class="tw-w-full" />
                </FormField>
                <div class="tw-grid tw-grid-cols-3 tw-mt-2">
                    <FormField class="tw-flex-1 tw-pr-1" label="Invoice Number">
                        <Textbox v-model="formData.invoice_number" class="tw-w-full" />
                    </FormField>
                    <FormField class="tw-flex-1 tw-px-1" label="Invoice Date">
                        <VueFlatPickr
                            v-model="formData.invoice_date"
                            placeholder="Required"
                            :config="flatpickrConfig"
                            class="input is-small"
                            required
                        />
                    </FormField>
                    <FormField class="tw-flex-1 tw-pl-1" label="Due Date">
                        <VueFlatPickr
                            v-model="formData.due_date"
                            placeholder="Required"
                            :config="flatpickrConfig"
                            class="input is-small"
                            required
                        />
                    </FormField>
                </div>
            </div>
            <DataTable
                class="tw-border-b"
                :data="formData.items"
                :columns="[
                    {
                        title: 'PO',
                        field: 'po',
                    },
                    {
                        title: 'Description',
                        field: 'description',
                    },
                    {
                        title: 'Qty',
                        field: 'quantity',
                    },
                    {
                        title: 'Unit Price',
                        field: 'unit_price_target',
                    },
                    {
                        title: 'Tax',
                        field: 'tax_rate_id',
                    },
                    {
                        title: 'Net Total',
                        field: 'net_total',
                        headerClass: '!tw-text-right',
                        class: '!tw-text-right tw-whitespace-nowrap',
                    },
                    {
                        title: 'Gross Total',
                        field: 'gross_total',
                        headerClass: '!tw-text-right',
                        class: '!tw-text-right tw-whitespace-nowrap',
                    },
                    {
                        title: 'Remaining (Gross)',
                        field: 'remaining_total',
                        headerClass: '!tw-text-right',
                        class: '!tw-text-right',
                    },
                    {
                        title: 'Amount (Gross)',
                        field: 'amount',
                        headerClass: '!tw-text-right',
                        class: '!tw-text-right',
                    },
                    {
                        field: 'actions',
                        class: '!tw-text-right',
                    },
                ]"
                :row-class="
                    (row) => {
                        return isNaN(row.max_value) ? 'tw-bg-emerald-50' : '';
                    }
                "
            >
                <template #heading(net_total)>
                    <InlineHelpLabel class="tw-inline-block" label="Net value of the line item">
                        Subtotal
                    </InlineHelpLabel>
                </template>
                <template #heading(gross_total)>
                    <InlineHelpLabel class="tw-inline-block" label="Gross value of the line item">
                        Total
                    </InlineHelpLabel>
                </template>
                <template #heading(remaining_total)>
                    <InlineHelpLabel class="tw-inline-block" label="The remaining gross amount you can invoice">
                        Remaining
                    </InlineHelpLabel>
                </template>
                <template #heading(amount)>
                    <InlineHelpLabel class="tw-inline-block" label="Gross amount to invoice">
                        Invoice Amount
                    </InlineHelpLabel>
                </template>
                <template #column(po)="{ row }">
                    <span class="tw-text-sm tw-text-gray-500">
                        {{ row.po_reference }}
                    </span>
                </template>
                <template #column(description)="{ index }">
                    <Textarea
                        v-model="formData.items[index].description"
                        maxlength="64"
                        placeholder="Enter a description"
                        :disabled="formData.items[index].is_withholding_tax"
                        expandable
                    />
                </template>
                <template #column(quantity)="{ index }">
                    <EditableText
                        v-model="formData.items[index].quantity"
                        type="number"
                        :min-value="0"
                        :decimal-places="0"
                        :editable="!formData.items[index].is_withholding_tax"
                        @update:model-value="recomputeInvoiceAmount(index)"
                    />
                </template>
                <template #column(unit_price_target)="{ index }">
                    <EditableText
                        v-model="formData.items[index].unit_price_target"
                        type="currency"
                        :min-value="0"
                        :decimal-places="2"
                        :currency-symbol="formatCurrency(0, 0, formData.target_currency).replace('0', '')"
                        :editable="!formData.items[index].is_withholding_tax"
                        @update:model-value="recomputeInvoiceAmount(index)"
                    />
                </template>
                <template #column(tax_rate_id)="{ index }">
                    <TaxRateSelector
                        v-model="formData.items[index].tax_rate_id"
                        type="expense"
                        compact
                        @update:model-value="recomputeInvoiceAmount(index)"
                    />
                </template>
                <template #column(net_total)="{ row }">
                    {{ formatCurrency(subtotal(row, 'unit_price_target'), 2, formData.target_currency) }}
                </template>
                <template #column(gross_total)="{ row }">
                    {{ formatCurrency(total(row, 'unit_price_target'), 2, formData.target_currency) }}
                </template>
                <template #column(remaining_total)="{ row }">
                    <span v-if="isNaN(row.max_value)" class="tw-text-gray-300">&mdash;</span>
                    <span v-else>
                        {{
                            formatCurrency(total(row, 'unit_price_target') - row.invoiced, 2, formData.target_currency)
                        }}
                    </span>
                </template>
                <template #column(amount)="{ row }">
                    <Currency
                        v-model="row.value"
                        :currency="formatCurrency(0, 0, formData.target_currency).replace('0', '')"
                    />
                </template>
                <template #column(actions)="{ index }">
                    <Button
                        v-if="!formData.items[index].is_withholding_tax"
                        compact
                        type="is-danger"
                        @click="formData.items.splice(index, 1)"
                    >
                        <i class="mdi mdi-delete"></i>
                    </Button>
                </template>
                <template #footer>
                    <tfoot>
                        <tr>
                            <td colspan="8">
                                <Checkbox
                                    v-if="xeroOrganisation && xeroOrganisation.config.withholding_tax_amount"
                                    v-model="withholdingTaxInfo.enable"
                                    :disabled="!!formData.items.find((item) => item.is_withholding_tax)"
                                    class="tw-w-fit"
                                    @update:model-value="
                                        (v) => {
                                            if (v) {
                                                formData.items.push({
                                                    description: 'Withholding Tax',
                                                    is_withholding_tax: true,
                                                    quantity: 1,
                                                    unit_price: xeroOrganisation.config.withholding_tax_amount,
                                                    tax_rate_id: null,
                                                    value: xeroOrganisation.config.withholding_tax_amount,
                                                });
                                                return;
                                            }
                                            formData.items = formData.items.filter((item) => !item.is_withholding_tax);
                                        }
                                    "
                                >
                                    Withholding Tax
                                </Checkbox>
                                <PercentageValue
                                    v-if="withholdingTaxInfo.enable"
                                    v-model="withholdingTaxInfo.percentage"
                                    class="tw-w-fit"
                                    name="withholding_tax_amount"
                                    force-percentage
                                    placeholder="20%"
                                />
                            </td>
                            <td class="tw-text-right">
                                {{ formatCurrency(totalValue, 2, formData.target_currency) }}
                            </td>
                            <td></td>
                        </tr>
                    </tfoot>
                </template>
            </DataTable>

            <div
                class="tw-grid tw-mt-2 tw-px-3 tw-gap-3"
                :class="{
                    'tw-grid-cols-3': formData.target_currency && formData.target_currency != baseCurrency,
                    'tw-grid-cols-2': !formData.target_currency || formData.target_currency == baseCurrency,
                }"
            >
                <FormField label="Source Currency">
                    {{ availableCurrencies[baseCurrency] }}
                </FormField>
                <FormField label="Target Currency">
                    {{ availableCurrencies[formData.target_currency] }}
                </FormField>
                <FormField
                    v-if="formData.target_currency && formData.target_currency != baseCurrency"
                    label="Conversion Rate"
                    field-class="tw-flex tw-gap-2"
                >
                    <NumberInput v-model="formData.conversion_rate" min="0" class="tw-flex-1" />
                    <Button @click="openCurrencyConverter()">
                        <i class="mdi mdi-calculator"></i>
                    </Button>
                </FormField>
            </div>

            <FormField class="tw-mt-4 tw-mx-3 tw-mb-3">
                <Upload
                    v-model="formData.files"
                    text="Upload documents"
                    :accept="[
                        'application/pdf',
                        '.xlsx',
                        '.doc',
                        '.docx',
                        'application/msword',
                        'application/vnd.ms-excel',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'image/*',
                    ]"
                    multiple
                />
                <div v-if="formData.files.length" class="tw-flex tw-gap-2 tw-flex-wrap tw-mt-2">
                    <Tag v-for="(file, index) in formData.files" :key="index" type="info">
                        {{ file.name }}
                        <Button type="is-link" class="!tw-p-0" @click="removeAttachment(index)">
                            <i class="mdi mdi-close"></i>
                        </Button>
                    </Tag>
                </div>
            </FormField>
            <template #footer>
                <Button :disabled="isLoading || isUploading" @click="close()">Cancel</Button>
                <FormField class="tw-pl-4" label="Supplier">
                    {{ formData.supplier_name }}
                </FormField>
                <div class="flex-fill"></div>
                <Button
                    type="is-success"
                    :disabled="isLoading || isUploading || !formData.purchase_orders.length"
                    @click="raiseInvoice()"
                >
                    Raise Invoice
                </Button>
            </template>
        </Modal>
        <ModalInvoicePaymentDates ref="paymentDatesModal" />
    </div>
</template>

<script>
import { useDataStore } from '@/js/stores/DataStore.js';
import Modal from '@/js/components/Modal.vue';
import EditableText from '@/js/components/controls/EditableText.vue';
import ModalInvoicePaymentDates from '@/js/components/modals/Invoices/InvoicePaymentDates.vue';
import Button from '@/js/components/controls/Button.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import NumberInput from '@/js/components/controls/NumberInput.vue';
import Upload from '@/js/components/controls/Upload.vue';
import DataTable from '@/js/components/tables/DataTable.vue';
import Textarea from '@/js/components/controls/Textarea.vue';
import TaxRateSelector from '@/js/components/controls/TaxRateSelector.vue';
import InlineHelpLabel from '@/js/components/widgets/InlineHelpLabel.vue';
import Currency from '@/js/components/controls/Currency.vue';
import Tag from '@/js/components/Tag.vue';
import { obj2form, toast, formatCurrency } from '@/js/utils.js';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';
import { subtotal, total } from '@/js/composables/useLineItemTotals.js';
import Checkbox from '@/js/components/controls/Checkbox.vue';
import PercentageValue from '@/js/components/widgets/PercentageValue.vue';
import ProgressBar from '@/js/components/ProgressBar.vue';
import LoadingSpinner from '@/js/components/widgets/LoadingSpinner.vue';
import VueFlatPickr from 'vue-flatpickr-component';

export default {
    components: {
        ModalInvoicePaymentDates,
        Modal,
        EditableText,
        PercentageValue,
        ProgressBar,
        LoadingSpinner,
        Button,
        FormField,
        Checkbox,
        Textbox,
        NumberInput,
        Upload,
        DataTable,
        Textarea,
        TaxRateSelector,
        InlineHelpLabel,
        Currency,
        Tag,
        VueFlatPickr,
    },
    emits: ['created'],
    data() {
        const store = useDataStore();
        return {
            isLoading: false,
            isUploading: false,
            uploadFileName: null,
            uploadProgress: 0,
            availableCurrencies: store.get('available_currencies'),
            baseCurrency: store.get('current_event.currency_code'),
            taxRates: store.get('account.tax_rates'),
            withholdingTaxInfo: {
                enable: false,
                percentage: store.get('account.xero_organisation.config.withholding_tax_amount'),
            },
            eventId: store.get('current_event.id'),
            xeroOrganisation: store.get('account.xero_organisation'),
            formData: {
                title: null,
                supplier_id: null,
                invoice_date: new Date().toISOString().split('T')[0],
                due_date: new Date().toISOString().split('T')[0],
                target_currency: JSON.parse(JSON.stringify(store.get('current_event.currency_code'))),
                conversion_rate: 1,
                items: [],
                files: [],
            },
            store,
        };
    },
    computed: {
        totalValue() {
            if (!this.formData.items) {
                return 0;
            }
            return this.formData.items.map((item) => parseFloat(item.value)).reduce((a, b) => a + b, 0);
        },
    },
    watch: {
        withholdingTaxInfo: {
            handler: function () {
                if (!this.withholdingTaxInfo.percentage || isNaN(this.withholdingTaxInfo.percentage)) {
                    return;
                }
                if (!this.formData.items.find((item) => item.is_withholding_tax)) {
                    return;
                }
                const totalItemsPrice = this.formData.items
                    .filter((item) => !item.is_withholding_tax)
                    .map((item) => parseFloat(item.unit_price))
                    .reduce((a, b) => a + b, 0);
                const percentageOfTotal = (this.withholdingTaxInfo.percentage / 100) * totalItemsPrice;
                const price = -Math.abs(percentageOfTotal);
                this.formData.items = this.formData.items.map((item) => {
                    if (item.is_withholding_tax) {
                        item.unit_price = price;
                        item.unit_price_target = price;
                        item.value = price;
                    }
                    return item;
                });
            },
            deep: true,
        },
    },
    mounted() {
        Eventbus.$on('purchaseInvoice:create', (purchaseOrders) => {
            this.open(purchaseOrders);
        });
    },
    methods: {
        subtotal,
        total,
        formatCurrency,
        open(purchaseOrders) {
            this.baseCurrency = purchaseOrders[0].base_currency;
            this.eventId = purchaseOrders[0].event_id;
            this.formData = {
                title: purchaseOrders.length === 1 ? purchaseOrders[0].title : null,
                supplier_id: purchaseOrders[0].supplier_id,
                supplier_name: purchaseOrders[0].supplier.name,
                invoice_date: new Date().toISOString().split('T')[0],
                due_date: new Date().toISOString().split('T')[0],
                target_currency: purchaseOrders[0].target_currency,
                conversion_rate: purchaseOrders[0].conversion_rate,
                items: purchaseOrders
                    .map((po) => {
                        return po.line_items.map((li) => {
                            return {
                                id: li.id,
                                is_withholding_tax: li.is_withholding_tax,
                                description: li.description,
                                quantity: li.quantity,
                                unit_price: parseFloat(li.unit_price ?? 0).toFixed(2),
                                unit_price_target: parseFloat(li.unit_price_target ?? 0).toFixed(2),
                                tax_rate_id: li.tax_rate_id ?? null,
                                value: this.lineItemRemaining(li),
                                max_value: this.lineItemRemaining(li),
                                invoiced: this.lineItemInvoiced(li),
                                po_reference: po.reference,
                            };
                        });
                    })
                    .flat(),
                files: [],
                purchase_orders: purchaseOrders.map((po) => po.id),
            };
            axios
                .get(
                    route('api.currency.convert', {
                        inputtext: '1',
                        convert_from: this.baseCurrency,
                        convert_to: this.formData.target_currency,
                    })
                )
                .then(({ data }) => (this.formData.conversion_rate = data.rate));
            // Because this method is enabled by mounted() it calls $refs before it has
            // been initialised, creating an 'undefined' console error.
            this.$refs.modal?.open();
        },
        reset() {
            this.isLoading = false;
            this.isUploading = false;
            this.uploadFileName = null;
            this.uploadProgress = 0;
            this.withholdingTaxInfo = {
                enable: false,
                percentage: 0,
            };
            this.formData = {
                title: null,
                supplier_id: null,
                invoice_date: new Date().toISOString().split('T')[0],
                due_date: new Date().toISOString().split('T')[0],
                target_currency: JSON.parse(JSON.stringify(this.baseCurrency)),
                conversion_rate: 1,
                items: [],
                files: [],
                purchase_orders: [],
            };
        },
        close() {
            this.reset();
            this.$refs.modal.close();
        },
        removeAttachment(index) {
            this.formData.files.splice(index, 1);
        },
        async raiseInvoice() {
            if (this.isLoading || this.isUploading) {
                return;
            }

            const files = [...this.formData.files];
            const purchaseOrders = [...this.formData.purchase_orders];
            const eventId = this.eventId;
            const invoiceData = { ...this.formData };
            delete invoiceData.files;

            this.isLoading = true;
            try {
                const response = await axios.post(
                    route('api.account.event.invoices', {
                        event: eventId,
                    }),
                    obj2form(invoiceData)
                );
                capturePosthogEvent('supplier_invoice_created');

                this.isLoading = false;
                this.isUploading = files.length > 0;
                const failedUploads = [];

                for (const file of files) {
                    this.uploadFileName = file.name;
                    this.uploadProgress = 0;

                    try {
                        await this.uploadAttachment(file, response.data.id, purchaseOrders[0]);
                    } catch {
                        failedUploads.push(file.name);
                    }
                }

                if (failedUploads.length) {
                    toast(
                        'Invoice Created',
                        `Your invoice was created, but these attachments could not be uploaded: ${failedUploads.join(', ')}`,
                        'warning'
                    );
                } else {
                    toast('Invoice Created', 'Your invoice has been created successfully', 'success');
                }

                Eventbus.$emit('supplierInvoice:created', {
                    supplierInvoice: response.data.id,
                    purchaseOrders,
                });
                this.$refs.paymentDatesModal.openModal({
                    event: eventId,
                    invoice: response.data.id,
                    purchaseOrder: purchaseOrders[0],
                });
                this.close();
            } finally {
                this.isLoading = false;
                this.isUploading = false;
            }
        },
        async uploadAttachment(file, supplierInvoiceId, purchaseOrderId) {
            const routeParams = {
                event: this.eventId,
                purchaseOrder: purchaseOrderId,
                supplierInvoice: supplierInvoiceId,
            };
            const { data: uploadHandshake } = await axios.post(
                route('api.account.event.purchaseorders.invoices.files.upload', routeParams),
                {
                    name: file.name,
                    size: file.size,
                }
            );

            if (!uploadHandshake?.action) {
                throw new Error('Unable to initialise the attachment upload.');
            }

            const uploadData = new FormData();
            Object.entries(uploadHandshake.inputs).forEach(([key, value]) => uploadData.append(key, value));
            uploadData.append('key', uploadHandshake.destination);
            uploadData.append('file', file);

            await axios.post(uploadHandshake.action, uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: ({ loaded, total }) => {
                    if (total) {
                        this.uploadProgress = Math.round((loaded / total) * 100);
                    }
                },
            });
            this.uploadProgress = 100;

            await axios.post(route('api.account.event.purchaseorders.invoices.files.completeUpload', routeParams), {
                internalSignature: uploadHandshake.internalSignature,
            });
        },
        recomputeInvoiceAmount(index) {
            let newValue =
                parseFloat(this.formData.items[index].unit_price_target) *
                parseFloat(this.formData.items[index].quantity);
            const taxRateId = this.formData.items[index].tax_rate_id;
            if (taxRateId) {
                const taxRate = this.store.get(`account.tax_rates.${taxRateId}`);
                if (taxRate) {
                    newValue = newValue * (1 + taxRate.rate / 100);
                }
            }
            this.formData.items[index].value = newValue;
        },
        lineItemRemaining(lineItem) {
            const totalInvoiced = this.lineItemInvoiced(lineItem);
            const totalTarget = this.total(lineItem, 'unit_price_target');
            return Math.round((totalTarget - totalInvoiced + Number.EPSILON) * 100) / 100;
        },
        lineItemInvoiced(lineItem) {
            let totalInvoiced = 0;
            const supplierInvoiceLineItems = lineItem.supplier_invoice_line_items || [];
            if (supplierInvoiceLineItems.length > 0) {
                totalInvoiced = supplierInvoiceLineItems.reduce(
                    (sum, item) => sum + parseFloat(item.total_value_target),
                    0
                );
            }
            return totalInvoiced;
        },

        openCurrencyConverter() {
            Eventbus.$emit('currencyConverter');
        },
    },
};
</script>
